import apiClient from './apiClient';
import { API_URL } from '../config';

export interface Doctor {
  id: number;
  name: string;
  npi: string | null;
  specialty: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface DoctorSearchResponse {
  items: Doctor[];
  total: number;
}

export interface DoctorTemplateBinding {
  id: number;
  doctor_id: number;
  cpt_code: string | null;
  modality: string | null;
  body_region: string | null;
  template_id: number | null;
  radiologist_template_id: number | null;
  custom_prompt_override: string | null;
  priority: number;
  status: string;
  created_by: number | null;
  approved_by: number | null;
  approved_at: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface BindingListResponse {
  items: DoctorTemplateBinding[];
  total: number;
}

export interface TemplateResolveResponse {
  found: boolean;
  binding_id: number | null;
  custom_prompt_override: string | null;
  template_id: number | null;
  radiologist_template_id: number | null;
  source: string;
}

export interface DoctorSearchParams {
  search?: string;
  limit?: number;
  offset?: number;
  include_inactive?: boolean;
}

const getDoctor = async (doctorId: number): Promise<Doctor> => {
  const response = await apiClient.get(`${API_URL}/doctors/${doctorId}`);
  return response.data;
};

const searchDoctors = async (params: DoctorSearchParams): Promise<DoctorSearchResponse> => {
  const response = await apiClient.get(`${API_URL}/doctors`, { params });
  return response.data;
};

const createDoctor = async (data: {
  name: string;
  npi?: string;
  specialty?: string;
}): Promise<Doctor> => {
  const response = await apiClient.post(`${API_URL}/doctors`, data);
  return response.data;
};

const updateDoctor = async (
  doctorId: number,
  data: { name?: string; npi?: string; specialty?: string },
): Promise<Doctor> => {
  const response = await apiClient.put(`${API_URL}/doctors/${doctorId}`, data);
  return response.data;
};

const deactivateDoctor = async (doctorId: number): Promise<void> => {
  await apiClient.delete(`${API_URL}/doctors/${doctorId}`);
};

const bulkImportDoctors = async (csvText: string): Promise<BulkImportResult> => {
  const response = await apiClient.post(`${API_URL}/doctors/bulk-import`, { csv_text: csvText });
  return response.data;
};

const listBindings = async (doctorId: number): Promise<BindingListResponse> => {
  const response = await apiClient.get(`${API_URL}/doctors/${doctorId}/bindings`);
  return response.data;
};

const createBinding = async (
  doctorId: number,
  data: {
    cpt_code?: string;
    modality?: string;
    body_region?: string;
    template_id?: number;
    radiologist_template_id?: number;
    custom_prompt_override?: string;
  },
): Promise<DoctorTemplateBinding> => {
  const response = await apiClient.post(`${API_URL}/doctors/${doctorId}/bindings`, data);
  return response.data;
};

const updateBinding = async (
  doctorId: number,
  bindingId: number,
  data: {
    custom_prompt_override?: string;
    template_id?: number;
    radiologist_template_id?: number;
    priority?: number;
    status?: string;
    cpt_code?: string;
    modality?: string;
    body_region?: string;
  },
): Promise<DoctorTemplateBinding> => {
  const response = await apiClient.put(`${API_URL}/doctors/${doctorId}/bindings/${bindingId}`, data);
  return response.data;
};

const deactivateBinding = async (doctorId: number, bindingId: number): Promise<void> => {
  await apiClient.delete(`${API_URL}/doctors/${doctorId}/bindings/${bindingId}`);
};

const resolveTemplate = async (
  doctorId: number,
  params: { cpt_code?: string; modality?: string; body_region?: string },
): Promise<TemplateResolveResponse> => {
  const response = await apiClient.get(`${API_URL}/doctors/${doctorId}/template`, { params });
  return response.data;
};

const saveAfterGeneration = async (
  doctorId: number,
  data: {
    cpt_code?: string;
    modality?: string;
    body_region?: string;
    custom_prompt_override: string;
    existing_binding_id?: number;
  },
): Promise<DoctorTemplateBinding> => {
  const response = await apiClient.post(
    `${API_URL}/doctors/${doctorId}/bindings/save-after-generation`,
    data,
  );
  return response.data;
};

const approveBinding = async (
  doctorId: number,
  bindingId: number,
): Promise<DoctorTemplateBinding> => {
  const response = await apiClient.post(
    `${API_URL}/doctors/${doctorId}/bindings/${bindingId}/approve`,
  );
  return response.data;
};

export interface BulkImportResult {
  created: number;
  skipped: number;
  errors: string[];
  created_doctors: Doctor[];
}

const doctorService = {
  getDoctor,
  searchDoctors,
  createDoctor,
  updateDoctor,
  deactivateDoctor,
  bulkImportDoctors,
  listBindings,
  createBinding,
  updateBinding,
  deactivateBinding,
  resolveTemplate,
  saveAfterGeneration,
  approveBinding,
};

export default doctorService;
